import {useMemo} from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {Table} from 'shells/table/table';
import {Body} from 'shells/typography/body';

import type {GetChatbotReport_chatbotReport_aiMetrics_oddballThreads as OddballThread} from 'nutshell-graphql-types';

import {ChatbotReportTableEmptyState} from './chatbot-report-table-empty-state';
import {THREAD_SIDEBAR_OPEN_SOURCES} from './consts';
import {useOpenThreadListSidebar} from './use-open-thread-list-sidebar';

interface Props {
    oddballThreads: OddballThread[];
}

export function OddballThreadsSection(props: Props) {
    const openThreadListSidebar = useOpenThreadListSidebar();

    const handleOpenThreads = (row: {original: OddballThread}) => {
        openThreadListSidebar({
            threadIds: row.original.threadIds,
            title: row.original.topic,
            subtitle: 'View recent chats on similar topics',
            source: THREAD_SIDEBAR_OPEN_SOURCES.ODDBALL_THREADS_SECTION,
        });
    };

    const columns = useMemo(
        () => [
            {
                id: 'topic',
                Header: 'Topic',
                accessor: (row: OddballThread) => ({
                    text: row.topic,
                    subtitle: row.description,
                    subtitleDefault: 'No description available',
                }),
                dataType: 'primaryWithSubtitle',
            },
            {
                id: 'threads',
                Header: 'Threads',
                accessor: (row: OddballThread) => row.threadCount,
                dataType: 'integer',
                width: 100,
            },
        ],
        []
    );

    const specialStates = useMemo(
        () => [
            {
                shouldRender: () => props.oddballThreads.length === 0,
                component: (
                    <ChatbotReportTableEmptyState text='Smooth sailing, every conversation stayed on track' />
                ),
            },
        ],
        [props.oddballThreads]
    );

    return (
        <Stack spacing={16}>
            <Stack spacing={4}>
                <Body isBold={true} color='dark'>
                    Oddball threads
                </Body>
                <Body size='small'>
                    Interesting conversations with chatbot that went off script
                </Body>
            </Stack>
            <Table
                // @ts-expect-error
                columns={columns}
                specialStates={specialStates}
                preserveHeaderInSpecialState={true}
                disableSorting={true}
                data={props.oddballThreads}
                hasHeaderBorder={true}
                hasHeaderCellBorders={false}
                isCompact={true}
                hasBorders={true}
                disableFilters={true}
                onSelectRow={handleOpenThreads}
                headerBackgroundColor='offWhiteDk'
                fullWidth={true}
            />
        </Stack>
    );
}
